import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import { useLanguage } from '../../context/LanguageContext';
import { useBrand } from '../../context/BrandContext';
import { useCompanyConfigContext } from '../../context/CompanyConfigContext';

interface OfferBarcodeDialogProps {
  open: boolean;
  onClose: () => void;
  offerNumber: string;
  offerName: string;
  endDate?: string;
  offerImage?: string;
}

const formatDate = (dateString?: string): string => 
  dateString?.replace(/(\d{2}:\d{2}):\d{2}/, '$1') || '';

const OfferBarcodeDialog: React.FC<OfferBarcodeDialogProps> = ({
  open,
  onClose,
  offerNumber,
  offerName,
  endDate,
  offerImage,
}) => {
  const { t } = useLanguage();
  const { brand } = useBrand();
  const { getConfig } = useCompanyConfigContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Récupérer la config qrcode.offer
  const useQRCode = getConfig('qrcode.offer') === '1' || getConfig('qrcode.offer') === 1;
  
  // URL du code-barres selon la configuration
  const barcodeUrl = useQRCode 
    ? `https://qrcode.b1-ge.fr/?size=5&url=${offerNumber}`
    : `https://ean.b1-ge.fr/barcode.php?encoding=EAN&scale=8&mode=png&IsNumberVisible=true&code=${offerNumber}`;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 2,
        borderBottom: '1px solid #e9ecef'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <i className="bx bx-gift" style={{ fontSize: 24, color: brand.primaryColor }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#495057' }}>
            {t('pages.offers.offerBarcode')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#74788d' }}>
          <i className="bx bx-x" style={{ fontSize: 24 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box>
          <Box sx={{ mb: 3, mt: 3, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#495057' }}>
              {offerName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Code : {offerNumber}
            </Typography>
            {endDate && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('pages.offers.validUntil')} : {formatDate(endDate)}
              </Typography>
            )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box
            sx={{
              textAlign: 'center',
              p: 2,
              backgroundColor: '#f8f8fb',
              borderRadius: 2,
              border: '1px solid #e9ecef',
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                p: 1.5,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                width: '100%',
              }}
            >
              <img
                src={barcodeUrl}
                alt={useQRCode ? "QR Code" : "Code-barres EAN"}
                style={{
                  width: useQRCode ? '250px' : '100%',
                  maxWidth: useQRCode ? '250px' : '100%',
                  height: 'auto',
                  display: 'block',
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML = `
                      <div style="width: 100%; min-height: 120px; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #f8f8fb; border-radius: 4px; border: 2px dashed #e9ecef;">
                        <i class="bx ${useQRCode ? 'bx-qr' : 'bx-barcode'}" style="font-size: 48px; color: #adb5bd; margin-bottom: 8px;"></i>
                        <span style="color: #adb5bd; font-size: 14px;">${useQRCode ? 'QR Code' : 'Code-barres'} non disponible</span>
                      </div>
                    `;
                  }
                }}
              />
            </Box>
            {offerImage && !useQRCode && (
              <Box
                sx={{
                  mt: 2,
                  backgroundColor: 'white',
                  p: 1.5,
                  borderRadius: 1,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  maxHeight: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={offerImage}
                  alt={offerName}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    maxHeight: '180px',
                    height: 'auto',
                    display: 'block',
                    borderRadius: '4px',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OfferBarcodeDialog;
